import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { Toaster } from 'sonner';
import Dashboard from './pages/Dashboard';
import JobsPage from './pages/JobsPage';
import ClientsPage from './pages/ClientsPage';
import WorkersPage from './pages/WorkersPage';
import WorkerView from './pages/WorkerView';
import LoginGate from './pages/LoginGate';
import HealthPage from './pages/HealthPage';
import {
  LayoutDashboard, Briefcase, Users, Building2, LogOut, HardHat, ShieldAlert
} from 'lucide-react';

function Nav() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const isWorker = user?.role === 'temp';

  const links = isWorker
    ? [{ to: '/my-jobs', icon: <Briefcase className="w-4 h-4" />, label: 'My Jobs' }]
    : [
        { to: '/',        icon: <LayoutDashboard className="w-4 h-4" />, label: 'Dispatch' },
        { to: '/jobs',    icon: <Briefcase className="w-4 h-4" />,       label: 'Jobs' },
        { to: '/clients', icon: <Building2 className="w-4 h-4" />,       label: 'Clients' },
        { to: '/workers', icon: <Users className="w-4 h-4" />,           label: 'Workers' },
        { to: '/health',  icon: <ShieldAlert className="w-4 h-4" />,      label: 'Health' },
      ];

  return (
    <header className="bg-gray-900 text-white sticky top-0 z-40 shadow">
      <div className="max-w-7xl mx-auto px-4 flex items-center h-14 gap-6">
        <Link to="/" className="font-bold text-blue-400 text-lg tracking-tight flex items-center gap-2 mr-4">
          <HardHat className="w-5 h-5" /> Opsive
        </Link>
        <nav className="flex items-center gap-1 flex-1 overflow-x-auto">
          {links.map(l => (
            <Link
              key={l.to}
              to={l.to}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                loc.pathname === l.to
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              {l.icon} {l.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-sm text-gray-400 hidden sm:block">{user?.name}</span>
          <button
            onClick={logout}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}

function ProtectedRoute({ children, managerOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (managerOnly && user.role === 'temp') return <Navigate to="/my-jobs" replace />;
  return children;
}

function AppShell() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen text-gray-400">Loading…</div>;

  return (
    <>
      {user && <Nav />}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Routes>
          <Route path="/login" element={<LoginGate />} />
          <Route path="/" element={<ProtectedRoute managerOnly><Dashboard /></ProtectedRoute>} />
          <Route path="/jobs" element={<ProtectedRoute managerOnly><JobsPage /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute managerOnly><ClientsPage /></ProtectedRoute>} />
          <Route path="/workers" element={<ProtectedRoute managerOnly><WorkersPage /></ProtectedRoute>} />
          <Route path="/health"  element={<ProtectedRoute managerOnly><HealthPage /></ProtectedRoute>} />
          <Route path="/my-jobs" element={<ProtectedRoute><WorkerView /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Toaster richColors position="top-right" />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </AuthProvider>
  );
}
